/**
 * The funder ledger: the single source of truth for how each dollar the
 * algorithm allocated splits into standard and matching money, and which
 * funder it belongs to.
 *
 * The recommender layer solves twice per budget portion (a group of funders
 * sharing one recommender and one exclusion set): phase 1 with the portion's
 * standard budget on standard MVFs, phase 2 with its full budget on matching
 * MVFs, seeded with phase 1's guarantees. Guarantees only grow between the
 * phases, but *accounts* (dollars actually paid) can fall: when another
 * portion raises its guarantee on a shared proposal, the cost of the levels
 * below is shared with it and this portion's bill drops, freeing standard
 * dollars that advance down its list. So "phase 2 minus phase 1" on an edge
 * is not matching money — it also contains reshuffled standard money.
 *
 * The ledger defines the split once, per portion, with these properties:
 *
 * - Every dollar the solver allocated appears exactly once (per proposal and
 *   per funder totals equal the solver's).
 * - A portion's matching dollars sum to its matching budget: the funders'
 *   matching inflow (inflow × ratio), capped by what phase 2 actually added
 *   over phase 1. Solver totals are not budgets — either phase can leave
 *   money unallocated — so neither bound alone is right: matching the solver
 *   never placed cannot be pledged, and standard money phase 1 stranded that
 *   phase 2 recovered is standard, not matching. The budget is spread over
 *   the proposals whose allocation grew, in proportion to the growth — except
 *   that a proposal which did not request a matching pledge never carries
 *   matching: its growth is standard funding, and any matching budget that
 *   reached such proposals is reported as `matchingSpentAsStandard` rather
 *   than pledged.
 * - Within a portion, matching splits across funders by their matching
 *   inflow (inflow × matching ratio), so a funder with matching turned off
 *   never holds matching; standard splits by standard inflow plus each
 *   funder's share of the unpledged matching budget, so a funder's total
 *   always equals what they sent the recommender.
 *
 * Every product surface that shows a standard/matching split (Funding Flow,
 * the allocation table, the announcement export) aggregates this ledger
 * rather than deriving its own split.
 */
import { formatCurrency } from "@lib/formatters";
import type NamedMatrix from "algorithm/NamedMatrix";

export type PhaseAmounts = { standard: number; matching: number };

export const ZERO_PHASE_AMOUNTS: Readonly<PhaseAmounts> = Object.freeze({
  standard: 0,
  matching: 0,
});

/** Read an aggregate, treating absent keys as zero dollars. */
export function ledgerAmounts(
  amountsByKey: { [key: string]: PhaseAmounts } | undefined,
  key: string,
): Readonly<PhaseAmounts> {
  return amountsByKey?.[key] ?? ZERO_PHASE_AMOUNTS;
}

export type FunderLedgerPortion = {
  id: string;
  recommenderId: string;
  funderIds: string[];
};

export type FunderLedgerInput = {
  portions: FunderLedgerPortion[];
  /** Phase-2 (with matching) accounts, one row per portion; hold column included. */
  portionAccounts: NamedMatrix<"portion", "proposal+hold">;
  /** Phase-1 (standard only) accounts, same shape as `portionAccounts`. */
  portionStandardAccounts: NamedMatrix<"portion", "proposal+hold">;
  /** Funder-layer accounts: how much of each funder's budget reached each recommender. */
  funderAccounts: NamedMatrix<"funder", "recommender+hold">;
  matchingBudgetRatios: { [funderId: string]: number };
  requestsMatchingPledge: (proposalId: string) => boolean;
  holdProposalId: string;
};

export type FunderLedger = {
  /** Funder ids in the algorithm's output order. */
  funderIds: string[];
  /** Recommender ids in the algorithm's output order, hold excluded. */
  recommenderIds: string[];
  /** Proposal ids in the algorithm's column order, hold included. */
  proposalIds: string[];
  holdProposalId: string;
  byFunderRecommenderProposal: {
    [funderId: string]: {
      [recommenderId: string]: { [proposalId: string]: PhaseAmounts };
    };
  };
  byRecommenderProposal: {
    [recommenderId: string]: { [proposalId: string]: PhaseAmounts };
  };
  byFunderRecommender: {
    [funderId: string]: { [recommenderId: string]: PhaseAmounts };
  };
  byFunderProposal: {
    [funderId: string]: { [proposalId: string]: PhaseAmounts };
  };
  byProposal: { [proposalId: string]: PhaseAmounts };
  byFunder: { [funderId: string]: PhaseAmounts };
  /**
   * Matching budget that the solver allocated to proposals not requesting a
   * matching pledge. It is counted as standard funding above, so total
   * matching pledged falls short of total matching budget by this amount.
   */
  matchingSpentAsStandard: number;
  /**
   * Standard budget the standard phase left unallocated that the matching
   * phase then placed. It is standard funding, never matching.
   */
  recoveredStandard: number;
  /** Matching budget the matching phase left unallocated; it is not pledged. */
  unallocatedMatching: number;
  warnings: string[];
};

// Dollar discrepancies below this are float noise, not findings.
const LEDGER_TOLERANCE = 1;

export type PortionSplit = {
  amounts: PhaseAmounts[];
  /**
   * Matching budget available to pledge: the nominal matching inflow, capped
   * by what phase 2 actually added over phase 1.
   */
  matchingBudget: number;
  /** The part of `matchingBudget` that landed on pledge-requesting proposals. */
  matchingPledged: number;
  /** Phase-2 growth beyond the nominal matching inflow: standard money phase 1
   * stranded and phase 2 recovered. */
  recoveredStandard: number;
  /** Nominal matching inflow phase 2 did not place anywhere. */
  unallocatedMatching: number;
};

/**
 * Split one portion's with-matching row into standard and matching dollars.
 * Matching is the portion's matching budget — `nominalMatchingBudget` (the
 * funders' inflow × ratio), capped by the realized growth from phase 1 to
 * phase 2 — spread over the pledge-requesting proposals whose allocation
 * grew, proportionally to the growth. The growth on pledge-requesting
 * proposals is always at least the budget that reached them, so no proposal's
 * matching exceeds its growth and no standard amount goes negative.
 */
export function splitPortionAccounts(
  standardRow: readonly number[],
  withMatchingRow: readonly number[],
  requestsMatchingPledge: readonly boolean[],
  nominalMatchingBudget: number,
): PortionSplit {
  let standardTotal = 0;
  let withMatchingTotal = 0;
  let pledgeableGrowth = 0;
  const growth = withMatchingRow.map((amount, index) => {
    const standardAmount = standardRow[index] ?? 0;
    standardTotal += standardAmount;
    withMatchingTotal += amount;
    const grew = Math.max(0, amount - standardAmount);
    if (requestsMatchingPledge[index]) {
      pledgeableGrowth += grew;
    }
    return grew;
  });

  const realizedGrowth = Math.max(0, withMatchingTotal - standardTotal);
  const nominal = Math.max(0, nominalMatchingBudget);
  const matchingBudget = Math.min(realizedGrowth, nominal);
  const scale =
    pledgeableGrowth > 0 ? Math.min(1, matchingBudget / pledgeableGrowth) : 0;
  const amounts = withMatchingRow.map((amount, index) => {
    const matching = requestsMatchingPledge[index]
      ? (growth[index] ?? 0) * scale
      : 0;
    return { standard: amount - matching, matching };
  });

  return {
    amounts,
    matchingBudget,
    matchingPledged: pledgeableGrowth * scale,
    recoveredStandard: Math.max(0, realizedGrowth - nominal),
    unallocatedMatching: Math.max(0, nominal - realizedGrowth),
  };
}

function addAmounts(
  target: { [key: string]: PhaseAmounts },
  key: string,
  amounts: PhaseAmounts,
): void {
  const entry = (target[key] ??= { standard: 0, matching: 0 });
  entry.standard += amounts.standard;
  entry.matching += amounts.matching;
}

export function buildFunderLedger(input: FunderLedgerInput): FunderLedger {
  const {
    portions,
    portionAccounts,
    portionStandardAccounts,
    funderAccounts,
    matchingBudgetRatios,
    holdProposalId,
  } = input;
  const proposalIds = portionAccounts.colNames;
  const requestsMatchingPledge = proposalIds.map((proposalId) =>
    input.requestsMatchingPledge(proposalId),
  );

  const ledger: FunderLedger = {
    funderIds: funderAccounts.rowNames,
    recommenderIds: funderAccounts.colNames.filter(
      (recommenderId) => recommenderId !== holdProposalId,
    ),
    proposalIds,
    holdProposalId,
    byFunderRecommenderProposal: {},
    byRecommenderProposal: {},
    byFunderRecommender: {},
    byFunderProposal: {},
    byProposal: {},
    byFunder: {},
    matchingSpentAsStandard: 0,
    recoveredStandard: 0,
    unallocatedMatching: 0,
    warnings: [],
  };

  // Money a funder parks on hold never enters the recommender layer, so no
  // portion carries it. Record it under the hold "recommender" split by the
  // funder's matching ratio, so funder-level aggregates cover the funder's
  // whole allocation.
  for (const funderId of ledger.funderIds) {
    const onHold = funderAccounts.getNames(funderId, holdProposalId);
    if (onHold === 0) {
      continue;
    }
    const matching = onHold * (matchingBudgetRatios[funderId] ?? 0);
    const amounts = { standard: onHold - matching, matching };
    addAmounts(
      (ledger.byFunderRecommender[funderId] ??= {}),
      holdProposalId,
      amounts,
    );
    addAmounts(ledger.byFunder, funderId, amounts);
  }

  for (const portion of portions) {
    if (portion.funderIds.length === 0) {
      continue;
    }

    // Each funder's inflow to this recommender, split by their matching
    // ratio into what they intended as standard vs matching money.
    const inflows = portion.funderIds.map((funderId) =>
      funderAccounts.getNames(funderId, portion.recommenderId),
    );
    const matchingInflows = inflows.map(
      (inflow, index) =>
        inflow * (matchingBudgetRatios[portion.funderIds[index]!] ?? 0),
    );
    const totalInflow = inflows.reduce((sum, inflow) => sum + inflow, 0);
    if (totalInflow <= 0) {
      continue;
    }
    const totalMatchingInflow = matchingInflows.reduce(
      (sum, inflow) => sum + inflow,
      0,
    );

    const split = splitPortionAccounts(
      portionStandardAccounts.getRowName(portion.id).toArray(),
      portionAccounts.getRowName(portion.id).toArray(),
      requestsMatchingPledge,
      totalMatchingInflow,
    );
    ledger.recoveredStandard += split.recoveredStandard;
    ledger.unallocatedMatching += split.unallocatedMatching;

    // Matching budget that reached non-requesting proposals is paid out as
    // standard funding, but it is still the matching funders' money.
    const unpledgedMatching = split.matchingBudget - split.matchingPledged;
    ledger.matchingSpentAsStandard += unpledgedMatching;

    const matchingShares = matchingInflows.map((inflow) =>
      totalMatchingInflow > 0 ? inflow / totalMatchingInflow : 0,
    );
    const standardWeights = inflows.map(
      (inflow, index) =>
        inflow -
        (matchingInflows[index] ?? 0) +
        unpledgedMatching * (matchingShares[index] ?? 0),
    );
    const totalStandardWeight = standardWeights.reduce(
      (sum, weight) => sum + weight,
      0,
    );
    const standardShares =
      totalStandardWeight > 0
        ? standardWeights.map((weight) => weight / totalStandardWeight)
        : inflows.map((inflow) => inflow / totalInflow);

    for (let col = 0; col < proposalIds.length; col++) {
      const proposalId = proposalIds[col]!;
      const edge = split.amounts[col]!;
      if (edge.standard === 0 && edge.matching === 0) {
        continue;
      }
      for (let index = 0; index < portion.funderIds.length; index++) {
        const funderId = portion.funderIds[index]!;
        const amounts: PhaseAmounts = {
          standard: edge.standard * (standardShares[index] ?? 0),
          matching: edge.matching * (matchingShares[index] ?? 0),
        };
        if (amounts.standard === 0 && amounts.matching === 0) {
          continue;
        }
        const byRecommender = (ledger.byFunderRecommenderProposal[funderId] ??=
          {});
        addAmounts(
          (byRecommender[portion.recommenderId] ??= {}),
          proposalId,
          amounts,
        );
        addAmounts(
          (ledger.byRecommenderProposal[portion.recommenderId] ??= {}),
          proposalId,
          amounts,
        );
        addAmounts(
          (ledger.byFunderRecommender[funderId] ??= {}),
          portion.recommenderId,
          amounts,
        );
        addAmounts(
          (ledger.byFunderProposal[funderId] ??= {}),
          proposalId,
          amounts,
        );
        addAmounts(ledger.byProposal, proposalId, amounts);
        addAmounts(ledger.byFunder, funderId, amounts);
      }
    }
  }

  if (ledger.recoveredStandard > LEDGER_TOLERANCE) {
    ledger.warnings.push(
      `${formatCurrency(ledger.recoveredStandard)} of standard budget that the standard phase left unallocated was allocated in the matching phase; it is counted as standard funding, not matching.`,
    );
  }
  if (ledger.unallocatedMatching > LEDGER_TOLERANCE) {
    ledger.warnings.push(
      `${formatCurrency(ledger.unallocatedMatching)} of matching budget was left unallocated by recommenders and is not pledged.`,
    );
  }
  if (ledger.matchingSpentAsStandard > LEDGER_TOLERANCE) {
    ledger.warnings.push(
      `${formatCurrency(ledger.matchingSpentAsStandard)} of matching budget went to proposals that did not request a matching pledge and is counted as standard funding.`,
    );
  }

  return ledger;
}
